import { Link } from 'react-router-dom';
import { Edit, Mail, MapPin, Globe, Clock, Shield } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { useAuthStore } from '@/stores/authStore';

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);

  if (!user) return null;

  const profile = user.profile;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">My Profile</h1>
        <Link to="/profile/me/edit">
          <Button variant="secondary" leftIcon={<Edit className="w-4 h-4" />}>
            Edit Profile
          </Button>
        </Link>
      </div>

      <Card>
        <CardBody>
          <div className="flex items-start gap-6">
            <Avatar
              src={profile?.avatar}
              name={profile?.displayName ?? user.email}
              size="xl"
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">
                {profile?.displayName ?? 'No display name'}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={user.role === 'AGENCY' ? 'purple' : 'primary'}>
                  {user.role}
                </Badge>
                <Badge variant={user.status === 'ACTIVE' ? 'success' : 'error'}>
                  {user.status}
                </Badge>
                {user.kyc?.status === 'APPROVED' && (
                  <Badge variant="success">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              {profile?.bio && (
                <p className="text-gray-400 mt-3">{profile.bio}</p>
              )}

              <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </span>
                {profile?.country && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.country}
                  </span>
                )}
                {profile?.availability && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {profile.availability}
                  </span>
                )}
                {profile?.isPublic && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    Public Profile
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {profile?.skills && profile.skills.length > 0 && (
        <Card>
          <CardHeader>Skills</CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill: any) => (
                <Badge key={skill} variant="primary">
                  {skill}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {profile?.languages && profile.languages.length > 0 && (
        <Card>
          <CardHeader>Languages</CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {profile.languages.map((lang: any) => (
                <Badge key={lang}>{lang}</Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {profile?.devices && profile.devices.length > 0 && (
        <Card>
          <CardHeader>Devices</CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {profile.devices.map((device: any) => (
                <Badge key={device}>{device}</Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {profile?.hourlyRate && (
        <Card>
          <CardHeader>Rate</CardHeader>
          <CardBody>
            <p className="text-2xl font-bold text-white">${profile.hourlyRate}/hr</p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
